/**
 * 选中框 / 手柄视觉常量。
 *
 * - **布局**（inset / pad / 热区）：世界坐标，数值 = 屏幕 px ÷ zoom，供 stage `scale(zoom)` 下定位。
 * - **描边与手柄方块**：由 `SelectionChromeFrame` / `SelectionHandleVisual` 用 `scale(1/zoom)` 固定屏幕 px（与 PageChrome 一致）。
 */

function clampZoom(zoom: number): number {
  return zoom > 0 ? zoom : 1;
}

/** 屏幕像素 → 世界坐标（画布逻辑 px） */
export function screenPxToWorld(screenPx: number, zoom: number): number {
  return screenPx / clampZoom(zoom);
}

/** 100% 时选中框线宽（屏幕 px） */
export const SELECTION_STROKE_SCREEN_PX = 1.5;

/** 100% 时元素边到外边框外侧的间距（屏幕 px） */
export const SELECTION_OUTER_GAP_SCREEN_PX = 6;

/** 100% 时手柄方块边长（屏幕 px，含描边） */
export const HANDLE_SIZE_SCREEN_PX = 10;

/** 100% 时手柄热区边长（屏幕 px） */
export const HANDLE_HIT_SIZE_SCREEN_PX = 18;

/** 100% 时手柄描边（屏幕 px） */
export const HANDLE_STROKE_SCREEN_PX = 1;

/** 100% 时旋转柄连线长度（屏幕 px） */
export const ROTATION_LINE_SCREEN_PX = 20;

/** 100% 时圆角柄热区直径（屏幕 px） */
export const RADIUS_HANDLE_HIT_SCREEN_PX = 14;

/** 100% 时圆角柄未拖拽时的最小展示半径（屏幕 px） */
export const RADIUS_HANDLE_REST_SCREEN_PX = 5;

/** 100% 时圆角柄中心圆点直径（屏幕 px） */
export const RADIUS_HANDLE_CENTER_DOT_SCREEN_PX = 3;

/** 100% 时页板外框拖拽条厚度（屏幕 px） */
export const PAGE_FRAME_DRAG_STRIP_SCREEN_PX = 6;

/** 100% 时页板尺寸标签与页面底边的间距（屏幕 px） */
export const PAGE_SIZE_LABEL_GAP_SCREEN_PX = 8;

export function selectionChromeWorldMetrics(zoom: number) {
  const z = clampZoom(zoom);
  const strokePx = screenPxToWorld(SELECTION_STROKE_SCREEN_PX, z);
  const outerGap = screenPxToWorld(SELECTION_OUTER_GAP_SCREEN_PX, z);
  const inset = outerGap - strokePx;
  return {
    strokePx,
    inset,
    pad: inset * 2,
    outerGap,
  };
}

export function handleChromeWorldMetrics(zoom: number) {
  const z = clampZoom(zoom);
  const strokePx = screenPxToWorld(HANDLE_STROKE_SCREEN_PX, z);
  const handleSizePx = screenPxToWorld(HANDLE_SIZE_SCREEN_PX, z);
  const hitSize = screenPxToWorld(HANDLE_HIT_SIZE_SCREEN_PX, z);
  const rotationLineHeight = screenPxToWorld(ROTATION_LINE_SCREEN_PX, z);
  return {
    strokePx,
    handleSizePx,
    hitSize,
    hitHalf: hitSize / 2,
    visualSizePx: handleSizePx - 2 * strokePx,
    rotationLineHeight,
    rotationHandleCenterY: -rotationLineHeight,
    rotationStrokePx: screenPxToWorld(SELECTION_STROKE_SCREEN_PX, z),
    radiusHitSize: screenPxToWorld(RADIUS_HANDLE_HIT_SCREEN_PX, z),
    radiusRest: screenPxToWorld(RADIUS_HANDLE_REST_SCREEN_PX, z),
    radiusCenterDotPx: screenPxToWorld(RADIUS_HANDLE_CENTER_DOT_SCREEN_PX, z),
  };
}
